// SPDX-License-Identifier: MIT
/**
 * Scene module contract (§7.5.4). Scene-kit modules and agent-written custom scenes implement the
 * same interface. `draw` must be a pure function of (props, env): no clocks, no Math.random, no I/O.
 */
import * as z from "zod/mini";
import type { ResolvedBrand } from './brand.ts';
import type { Style } from './style.ts';
export type Aspect = '16:9' | '9:16' | '1:1';
/** Design-space size every scene draws into; the compositor scales it to the output resolution. */
export const DESIGN_W = 1920;
export const DESIGN_H = 1080;
/**
 * Optional QA hints from a scene, in the scene's current coordinates. Text is captured automatically
 * in QA sessions; scenes report what cannot be seen from draw calls, such as where the logo is.
 */
export interface QAReporter {
    text(id: string, box: [
        x: number,
        y: number,
        w: number,
        h: number
    ], alpha: number, text?: string): void;
    /** Non-text elements by role, e.g. `element('logo', box)` wherever the brand mark is drawn. */
    element(id: string, box: [
        x: number,
        y: number,
        w: number,
        h: number
    ]): void;
}
export interface SceneEnv {
    /** Global time (seconds). */
    t: number;
    /** Time since the scene started (seconds). */
    local: number;
    /** Global beat (float). */
    beat: number;
    /** Beat since the scene started (float). */
    localBeat: number;
    /** Scene duration (seconds). */
    dur: number;
    /** Seconds per beat. */
    spb: number;
    /** Output size in pixels. Scenes draw in DESIGN_W × DESIGN_H units regardless. */
    size: [
        number,
        number
    ];
    brand: ResolvedBrand;
    style: Style;
    /** Seeded RNG scoped to this scene. Never use Math.random. */
    rng: (key: string) => () => number;
    /**
     * Draw into the crisp layer: composited over bloom, chromatic aberration and lens distortion,
     * but still graded, vignetted and motion-blurred. Use it for copy that must stay sharp.
     * `draw` gets a context with the scene's current transform and opacity (clips are not carried
     * over; re-apply masks inside). During transitions it draws into the frame as usual, so wipes
     * and irises still cover the text.
     */
    crisp: (draw: (ctx: CanvasRenderingContext2D) => void) => void;
    report?: QAReporter;
}
/** A scene entry's length in beats and the reel's seconds per beat. */
export type SceneTiming = {
    beats: number;
    spb: number;
};
export type SceneSoundEvent = {
    beat: number;
    sfx: 'impact' | 'whoosh' | 'riser' | 'blip' | 'tick' | 'glitch';
    gain?: number;
};
/** What a scene may change about the look of its own frames. Omitted fields keep the defaults. */
export type PostOverride = Partial<{
    /** Extra white flash 0–1 (combined with cue flashes by max). */
    flash: number;
    /** Extra aberration hit (combined by max). */
    aberration: number;
    /** Extra camera shake strength (combined by max). */
    shake: number;
    /** Bloom intensity multiplier. */
    bloom: number;
    /** Exposure multiplier in linear light. */
    exposure: number;
    /** Contrast around mid-grey (1 = neutral). */
    contrast: number;
    /** Saturation (0 = monochrome, 1 = neutral). */
    saturation: number;
    /** Per-channel multiplier, e.g. [1.05, 1, 0.92] for warm. */
    tint: [
        number,
        number,
        number
    ];
    /** Vignette multiplier. */
    vignette: number;
    /** Grain multiplier. */
    grain: number;
    /**
     * Scale of the style's lens (its base chromatic aberration and distortion), 0–1. 0 gives a clean,
     * flat print look, e.g. for fine lines or small type on a light ground; beat hits scale with it.
     */
    lens: number;
}>;
export interface SceneModule<P = Record<string, unknown>> {
    id: string;
    version: string;
    propsSchema: z.ZodType<P>;
    aspects: Aspect[];
    draw(ctx: CanvasRenderingContext2D, props: P, env: SceneEnv): void;
    /** 0–1 motion intensity, used for adaptive motion blur (§7.8.4). */
    motion?(props: P, env: SceneEnv): number;
    /**
     * Sounds this scene asks the soundtrack for, in the scene's local beats (counter blips, slot
     * ticks, extra hits). Pure function of props and the scene's timing (its length in beats and
     * seconds per beat, for choreography that scales with them); the soundtrack places them
     * sample-accurately.
     */
    audioEvents?(props: P, timing: SceneTiming): SceneSoundEvent[];
    /** Shape the post look over the scene's own time (pure function of props and env). */
    post?(props: P, env: SceneEnv): PostOverride;
    /** Text the scene may draw — the runtime preloads the fonts for it before rendering. */
    glyphs?(props: P): string;
    /**
     * What the scene says the product does or achieves, as drawn: button actions, step titles,
     * notifications, feature names. QA checks each against the brand's facts and copy (FR-4.5), the
     * way numbers are checked. Generic interface words ("Search", "Save") need not be left out.
     */
    claims?(props: P): string[];
}
/** One claim of one scene entry, as `claims(props)` returned it. */
export type SceneClaim = {
    scene: string;
    text: string;
};
/** Helper that keeps prop types inferred from the schema. */
export function defineScene<S extends z.ZodType>(scene: Omit<SceneModule<z.infer<S>>, 'propsSchema'> & {
    propsSchema: S;
}): SceneModule<z.infer<S>> {
    return scene as SceneModule<z.infer<S>>;
}
